import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import inflationService from 'data/inflation'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Increase = {
  propertyid: number
  increaseid: number
}
// private module logic //

const id: string = "increase"
const initialState: ApiResult<Increase[]> = { id, state: "init", value: [] }
const increaseStream = new BehaviorSubject<ApiResult<Increase[]>>(initialState)
const increaseEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let increaseMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function sortRent(data: Increase[]): Increase[] {
  data.sort((a, b) => {
    let A = a["code"], B = b["code"]

    if(A > B) return 1
    else if(A < B) return -1
    else return 0
  });

  return data
}

function clear(): void {
  increaseStream.next({ id, state: 'ok', value: [] })
  increaseMap = {}
}

function get(increaseid: number): Increase {
  return increaseMap[increaseid]
}

function refresh(): Promise<any> {
  const currentValue = increaseStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchIncrease(propertyid)
}

function fetchIncrease(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Increase[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  increaseStream.next({ id, state: 'busy', value: increaseStream.getValue().value })

  let url = `increase?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `increase?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Increase[]) => {
    _.forEach(records, (increase) => (increaseMap[increase.increaseid] = increase))
    // merge results unique by increaseid
    increaseStream.next({ id, state: 'ok', value: sortRent(records) })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number, defaults: {}): Promise<Increase | { message: string }> {
  const currentValue = increaseStream.getValue().value
  increaseStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    amt: null,
    amttype: defaults['increaseType'],
    inflationdetail: defaults['increaseType'] === "/Area/Yr Annually" ? defaults['increaseInflation'] : null,
    effectivemonth: "Lease Year",
    inputtype: 'basic',
    name: '',
    increasedetail: null
  }

  return Client.post(`increase`, payload)
    .then((increase: Increase) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, increase.increaseid),
        redofunc: () => create(propertyid, defaults)
      })
      increaseMap[increase.increaseid] = increase
      // merge results unique by increaseid
      const currentValue = increaseStream.getValue().value
      increaseStream.next({
        id,
        state: 'ok',
        value: _.unionBy([increase], currentValue, 'increaseid')
      })
      increaseEventStream.next({ type: 'INCREASE_CREATED', payload: increase })
      return increase
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      increaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchIncrease(propertyid)
      return err
    })
}

function copy(propertyid:number, increaseid:number, code:string) {
  const currentValue = increaseStream.getValue().value;
  increaseStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'increase', recordid: increaseid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((increase: Increase) => {
      const newRecord = increase[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.increaseid),
        redofunc: () => copy(propertyid, increaseid, code)
      });
      increaseMap[newRecord.increaseid] = newRecord
      // merge results unique by increaseid
      const currentValue = increaseStream.getValue().value
      increaseStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'increaseid').sort((a, b) => alphanum(a.code, b.code))
      })
      increaseEventStream.next({ type: 'INCREASE_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      increaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchIncrease(propertyid)
      return err
    })
}

function copies(propertyid: number, increaseid: number, count: number) {
  const currentValue = increaseStream.getValue().value;
  increaseStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'increase', recordid: increaseid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((increases: Increase[]) => {
      push_undo({
        undofunc: () => _.forEach(increases, newRecord => deleteOne(propertyid, newRecord.increaseid)),
        redofunc: () => copies(propertyid, increaseid, count)
      });
      _.forEach(increases, newRecord => increaseMap[newRecord.increaseid] = newRecord)
      // merge results unique by increaseid
      const currentValue = increaseStream.getValue().value
      increaseStream.next({
        id,
        state: 'ok',
        value: _.unionBy(increases, currentValue, 'increaseid').sort((a, b) => alphanum(a.code, b.code))
      })
      increaseEventStream.next({ type: 'INCREASE_COPIED', payload: increases })
      return increases
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      increaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchIncrease(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  increaseid: number,
): Promise<boolean | { message: string }> {
  const currentValue = increaseStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, increaseid)
  const refreshFn = () => fetchIncrease(propertyid)
  if (!(await handleReferences('increase', increaseid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted increaseid
  const value = _(currentValue)
    .filter((p) => p.increaseid !== increaseid)
    .value()
  value.forEach((p) => (increaseMap[p.increaseid] = p))

  increaseStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(increaseid).then(() =>
  Client.del(`increase?increaseid=eq.${increaseid}`)
    .then((increase: Increase) => {
      delete increaseMap[increase.increaseid]
      push_undo({
        undofunc: () => {
          Client.post('increase', increase)
          increaseMap[increase.increaseid] = increase
          increaseStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, increaseid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      increaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchIncrease(propertyid)
      return err
    })
  )
}

function patch(increase: Increase): Promise<Increase | { message: string }> {
  const currentValue = increaseStream.getValue().value
  const increaseid: number = increase.increaseid
  const curincreaseincrease = _.find(currentValue, { increaseid })
  const changeset = diff_obj(increase, curincreaseincrease)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(curincreaseincrease),
    redofunc: () => patch(increase)
  })

  // optimistic update
  increaseStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {increaseid}), increase)
  })

  return _patches_.watch(increaseid, Client.patch(`increase?increaseid=eq.${increaseid}`, changeset)
    .then((mf: Increase[]) => {
      _.forEach(mf, (increase) => (increaseMap[increase.increaseid] = increase))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      increaseStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(curincreaseincrease, 'propertyid')
      if (propertyid) fetchIncrease(propertyid)
      return err
    }))

}

// public module interface //

const increaseService = {
  stream: () => increaseStream.asObservable(),
  value: () => increaseStream.getValue().value,
  eventStream: () => increaseEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchIncrease,
  patch,
  refresh
}

export default increaseService
